import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ArrowLeft, UserPlus } from "lucide-react";
import { FormattedText, FormRenderer } from "@sga/shared/form-renderer";
import { SEPLAT_BRAND_COLORS, SEPLAT_BRAND_FONTS } from "@sga/shared";
import { ApiError, apiGet, apiPost } from "../lib/api.js";
import { Panel } from "../components/Panel.js";

/**
 * Entering a registration for someone who cannot enter their own — at a venue, or over the
 * support line.
 *
 * Three steps on one screen, in the order the conversation actually goes: who is this for, then
 * the questions, then consent. All the questions at once rather than paged, because staff are
 * reading them aloud down a phone and paging is the wrong shape for that.
 *
 * The form is the real live form, fetched from the same serializer the applicant's browser gets.
 * A separately-maintained staff version of a form is how the two drift.
 */

interface SchemaPayload {
  form: { id: string; slug: string; title: string; status: string };
  schema: { fields: never[]; sections: never[]; logic: never[] };
  privacyNotice: string | null;
}

type Identity =
  | {
      status: "resolved";
      respondentId: string;
      email: string;
      isPlaceholder: boolean;
      alreadyRegistered: { reference: string | null; submittedAt: string | null } | null;
    }
  | {
      status: "needs_choice";
      existing: { respondentId: string; displayName: string | null; maskedEmail: string };
    };

export const AssistedEntry = ({
  formId,
  formTitle,
  onBack,
}: {
  formId: string;
  formTitle: string;
  onBack: () => void;
}): JSX.Element => {
  const [person, setPerson] = useState({ fullName: "", phone: "", email: "" });
  const [identity, setIdentity] = useState<Identity | null>(null);
  const [values, setValues] = useState<Record<string, unknown>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [attested, setAttested] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<{ reference: string | null } | null>(null);

  const schemaQuery = useQuery({
    queryKey: ["assisted-schema", formId],
    queryFn: () => apiGet<SchemaPayload>(`/forms/${formId}/assisted/schema`),
  });

  const identify = useMutation({
    mutationFn: (onCollision?: "same" | "different") =>
      apiPost<Identity>(`/forms/${formId}/assisted/identity`, {
        fullName: person.fullName,
        phone: person.phone,
        email: person.email.trim() || null,
        ...(onCollision ? { onCollision } : {}),
      }),
    onSuccess: (result) => {
      setError(null);
      setIdentity(result);
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : "Could not look that person up."),
  });

  const save = useMutation({
    mutationFn: () =>
      apiPost<{ reference: string | null }>(`/forms/${formId}/assisted/submit`, {
        respondentId: identity?.status === "resolved" ? identity.respondentId : "",
        data: values,
        consents: [],
        consentAttested: true,
      }),
    onSuccess: (result) => {
      setError(null);
      setSaved(result);
    },
    onError: (caught) => {
      const details = (caught as { details?: { issues?: { key: string; message: string }[] } })
        .details;
      if (details?.issues?.length) {
        setErrors(Object.fromEntries(details.issues.map((i) => [i.key, i.message])));
        setError("Some answers need correcting.");
      } else {
        setError(caught instanceof ApiError ? caught.message : "Could not save this registration.");
      }
    },
  });

  const resolved = identity?.status === "resolved" ? identity : null;

  if (saved) {
    return (
      <div className="space-y-4">
        <Panel title="Registration saved">
          <p className="text-sm">
            <strong>{person.fullName}</strong> is registered for {formTitle}.
          </p>
          {saved.reference && (
            <p className="mt-3 text-sm">
              Read this reference back to them — it is their only receipt, since a registration
              taken this way sends no confirmation email:
              <span className="mt-2 block font-mono text-2xl font-semibold">{saved.reference}</span>
            </p>
          )}
          <div className="mt-4 flex gap-2">
            <button
              type="button"
              onClick={() => {
                setPerson({ fullName: "", phone: "", email: "" });
                setIdentity(null);
                setValues({});
                setAttested(false);
                setSaved(null);
              }}
              className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground"
            >
              Enter another
            </button>
            <button
              type="button"
              onClick={onBack}
              className="rounded-md border px-3 py-1.5 text-sm hover:bg-muted"
            >
              Back to submissions
            </button>
          </div>
        </Panel>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-1 rounded-md border px-2.5 py-1.5 text-sm hover:bg-muted"
        >
          <ArrowLeft aria-hidden className="h-4 w-4" />
          Submissions
        </button>
        <div className="min-w-0">
          <h1 className="font-heading truncate text-lg font-semibold">Enter a registration</h1>
          <p className="truncate text-xs text-muted-foreground">
            {formTitle} — on behalf of someone who cannot fill it in themselves
          </p>
        </div>
      </div>

      {error && (
        <p className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800">
          {error}
        </p>
      )}

      <Panel title="1. Who is this for">
        <div className="grid gap-3 sm:grid-cols-3">
          <label className="text-sm">
            <span className="mb-1 block text-xs font-medium text-muted-foreground">Full name</span>
            <input
              value={person.fullName}
              onChange={(event) => setPerson({ ...person, fullName: event.target.value })}
              disabled={resolved !== null}
              className="w-full rounded-md border bg-background px-3 py-2 text-sm disabled:opacity-60"
            />
          </label>
          <label className="text-sm">
            <span className="mb-1 block text-xs font-medium text-muted-foreground">Phone</span>
            <input
              value={person.phone}
              onChange={(event) => setPerson({ ...person, phone: event.target.value })}
              disabled={resolved !== null}
              placeholder="0803 123 4567"
              className="w-full rounded-md border bg-background px-3 py-2 text-sm disabled:opacity-60"
            />
          </label>
          <label className="text-sm">
            <span className="mb-1 block text-xs font-medium text-muted-foreground">
              Email (if they have one)
            </span>
            <input
              value={person.email}
              onChange={(event) => setPerson({ ...person, email: event.target.value })}
              disabled={resolved !== null}
              placeholder="Leave blank if none"
              className="w-full rounded-md border bg-background px-3 py-2 text-sm disabled:opacity-60"
            />
          </label>
        </div>

        {identity?.status === "needs_choice" && (
          // One handset serves a family. Answering this silently either way is wrong: reusing the
          // record merges two people, minting a second breaks the one-per-person rule for the first.
          <div className="mt-3 rounded-md border border-amber-400 bg-amber-50 p-3 text-sm text-amber-900">
            <p className="font-medium">
              Someone is already registered on that phone number
              {identity.existing.displayName ? `: ${identity.existing.displayName}` : ""} (
              {identity.existing.maskedEmail}).
            </p>
            <p className="mt-1">Is this the same person?</p>
            <div className="mt-2 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => identify.mutate("same")}
                className="rounded-md border border-amber-500 bg-white px-3 py-1.5 text-sm font-medium"
              >
                Yes — the same person
              </button>
              <button
                type="button"
                onClick={() => identify.mutate("different")}
                className="rounded-md border border-amber-500 bg-white px-3 py-1.5 text-sm font-medium"
              >
                No — someone else on that phone
              </button>
            </div>
          </div>
        )}

        {resolved?.alreadyRegistered && (
          <p className="mt-3 rounded-md border border-amber-400 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            This person already has a registration on this form
            {resolved.alreadyRegistered.reference
              ? ` (${resolved.alreadyRegistered.reference})`
              : ""}
            . Saving another will be refused.
          </p>
        )}

        {resolved && (
          <p className="mt-3 text-xs text-muted-foreground">
            {resolved.isPlaceholder
              ? "No email address, so this person cannot receive a confirmation or use the applicant portal. Read them their reference number at the end."
              : `Confirmation will go to ${resolved.email}.`}
          </p>
        )}

        {!resolved && (
          <button
            type="button"
            disabled={!person.fullName.trim() || !person.phone.trim() || identify.isPending}
            onClick={() => identify.mutate(undefined)}
            className="mt-3 flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground disabled:opacity-50"
          >
            <UserPlus aria-hidden className="h-4 w-4" />
            {identify.isPending ? "Checking…" : "Continue"}
          </button>
        )}
      </Panel>

      {resolved && schemaQuery.data && (
        <>
          <Panel
            title="2. Their answers"
            subtitle="Read each question to them and record the answer."
          >
            <FormRenderer
              fields={schemaQuery.data.schema.fields}
              sections={schemaQuery.data.schema.sections}
              logic={schemaQuery.data.schema.logic}
              values={values}
              errors={errors}
              onChange={(key, value) => setValues((current) => ({ ...current, [key]: value }))}
              branding={{
                ...SEPLAT_BRAND_COLORS,
                headingFont: SEPLAT_BRAND_FONTS.stack,
                bodyFont: SEPLAT_BRAND_FONTS.stack,
              }}
              lookups={{ states: [], lgasForState: () => [] }}
            />
          </Panel>

          <Panel title="3. Consent">
            {schemaQuery.data.privacyNotice && (
              <div className="mb-3 max-h-56 overflow-y-auto rounded-md border bg-muted/30 p-3 text-xs">
                <FormattedText text={schemaQuery.data.privacyNotice} />
              </div>
            )}

            <label className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                checked={attested}
                onChange={(event) => setAttested(event.target.checked)}
                className="mt-1"
              />
              <span>
                I read this notice to the applicant and they agreed to it.
                <span className="block text-xs text-muted-foreground">
                  Recorded against your name and the time — a record saying the applicant ticked a
                  box themselves would not be true.
                </span>
              </span>
            </label>

            <button
              type="button"
              disabled={!attested || save.isPending}
              onClick={() => save.mutate()}
              className="mt-4 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50"
            >
              {save.isPending ? "Saving…" : "Save this registration"}
            </button>
          </Panel>
        </>
      )}
    </div>
  );
};
